/**
 * Flow editor — main shell.
 *
 * Three-column layout: left controls / center 3D scene + timeline / right
 * panel (difficulty + auto-map placeholder). Wires the local flowStore to
 * the editorStore's `activeView` for the Back button.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import {
  useFlowStore, liveLaneHeld, liveHitSuppress,
  playTestHitIds, playTestJudgedIds, playTestSparkQueue, playTestHolds, resetPlayTestRun,
} from './flowStore';
import { FLOW_DIFFICULTIES, laneCountFor, type FlowDifficultyName, type FlowNote, type FlowLaneCount } from './types';
import { NOTE_TYPE_INFO, FLICK_NOTE_INFO } from './flowNoteTypes';
import { loadAudioFile } from './audioLoader';
import { importSynthToFlow } from './synthImport';
import { detectAudioBpm, detectAudioOffset, detectAudioBeatOffset } from './audioAutoDetect';
import {
  downloadCurrentChart,
  loadChartFromFile,
  saveChartToServer,
  buildFlowBundle,
  FlowChartLoadError,
} from './chartIO';
import {
  isFsAccessSupported,
  restoreFolder,
  subscribeFolder,
  connectedFolderName,
  pickFolder,
  writeFolderChart,
  readFolderChart,
} from './flowFsAccess';
import { attachDraftAutosave, restoreDraftIfAny } from './flowDraft';
import FlowScene from './FlowScene';
import PerfPill from '../../components/editor/PerfPill';
import VisualizerBackground from '../../viz/VisualizerBackground';
import VisualizerPanel from '../../viz/VisualizerPanel';
import FlowTimeline from './FlowTimeline';
import FlowTransportPanel from './FlowTransportPanel';
import FlowSettingsDialog from './FlowSettingsDialog';
import BenchmarkOverlay from '../../perf/BenchmarkOverlay';
import AudioBars from '../../components/editor/AudioBars';
import VolumeControls from '../../components/ui/VolumeControls';
import DifficultyBar from '../../components/ui/DifficultyBar';
import ControlsPanel from '../../components/ui/ControlsPanel';
import AutoBpmDialog from '../../components/ui/AutoBpmDialog';
import { Group } from './FlowGroup';
import { usePanelLayout } from '../../components/ui/panelLayoutStore';
import { IcSave, IcArrowUL, IcArrowUp, IcArrowUR, IcUpload, IcClose, IcSpectrum, IcSettings, IcPhone, IcFullscreen, IcKeyboard } from '../../components/ui/icons';
import { Tooltip, tip } from '../../components/ui/Tooltip';
import ScrollArrows from '../../components/ui/ScrollArrows';
import ScrollFade from '../../components/ui/ScrollFade';
import OverlayScrollbar from '../../components/ui/OverlayScrollbar';
import MapGridButtons from '../../components/ui/MapGridButtons';
import ExportVisualizerDialog from '../../components/ui/ExportVisualizerDialog';
import EditorBackHeader from '../../components/ui/EditorBackHeader';
import EditorPlaybackBar from '../../components/ui/EditorPlaybackBar';
import FlowMinimap from './FlowMinimap';
import FlowMinimapCamWindow from './FlowMinimapCamWindow';
import { SaveAsDialog, LoadLibraryDialog, NewMapDialog, EditMapInfoDialog } from './FlowDialogs';
import { QRCodeSVG } from 'qrcode.react';
import { setTickVolume } from '../../audio/tickSound';
import { setFlowAudioVolume, playLaneHit, scheduleLaneHitAt, setScrollSound, setScrollVolume, setNoteSound, setNoteVolume, setLaneSoundUniform, setPlaceSound, setDeleteSound, setPlaceVolume, setDeleteVolume, getFlowAudioContext } from './flowSounds';
import { matchFlowAction, prettyCombo, laneKeyMap, KEYBIND_SECTIONS, MOUSE_REFERENCE, type FlowAction } from './flowKeybindings';
import { PLAY_TEST_HIT_MS, EDIT_WINDOW_BEATS, SNAP_OPTIONS } from './flowSceneConstants';
import { setSyncProbeEnabled, probe } from './flowSyncProbe';
import FloatingWindow from './FloatingWindow';
import * as engine from './flowAudioEngine';
import { color, font, ui } from '../../components/ui/theme';
import {
  debugBodyStyle,
  debugBtnStyle,
  debugStatsRowStyle,
  debugStatStyle,
  debugStatLabel,
  debugStatVal,
  shellStyle,
  leftPanelStyle,
  topScrollStyle,
  rightPanelStyle,
  centerStyle,
  highwayHolder,
  bg3dLayerStyle,
  offsetWarnStyle,
  chartDropOverlayStyle,
  chartDropCardStyle,
  bpmApplyBtnStyle,
  bpmPopupBackdrop,
  bpmPopupCard,
  bpmPopupTitle,
  bpmPopupSub,
  bpmPopupRow,
  brushRowStyle,
  brushCardStyle,
  brushCardTitleStyle,
  brushCardDescStyle,
  dirNoteRowStyle,
  dirNoteBtnStyle,
  dirNoteGlyphStyle,
  dirNoteLabelStyle,
  cameraGridStyle,
  cameraBtnStyle,
  cameraActionBtnStyle,
  bottomBtnsStyle,
  toastStyle,
  toastErrorStyle,
  helpBarStyle,
  flowBtnState,
  vizGroupBtnStyle,
  vizChipStyle,
  vizChipNameStyle,
  vizChipRemoveStyle,
  reactSegStyle,
  reactSegLabelStyle,
  reactSegBtnStyle,
  reactSegBtnActiveStyle,
  sidebarScrollStyle,
} from './FlowApp.styles';

// Visualizer manual-camera zoom range (0.1 = wide zoom-out, 3 = zoomed in).
// Min lowered 0.2 → 0.1 so the camera can pull 50% further back.
const VIZ_ZOOM_MIN = 0.1;
const VIZ_ZOOM_MAX = 3;

// A circular header button (Back / fullscreen / play-on-phone / settings) —
// shared style; accent-tinted when `active`.
function headerCircleBtn(active: boolean): React.CSSProperties {
  return {
    flexShrink: 0, width: 38, height: 38, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: active ? 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.2)' : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
    border: active ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)' : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
    color: 'var(--accent)',
    cursor: 'pointer',
  };
}

/** Flick brush key → directional icon (replaces the ↖ ↑ ↗ glyphs). */
const FLICK_ICON = {
  'arrow-left': IcArrowUL,
  'arrow-center': IcArrowUp,
  'arrow-right': IcArrowUR,
} as const;

/** Note-brush key → the rebindable keyboard action that selects it. */
const NOTE_BRUSH_ACTION: Record<string, FlowAction> = {
  'tap':          'brushTap',
  'trail':        'brushTrail',
  'arrow-left':   'brushArrowL',
  'arrow-center': 'brushArrowC',
  'arrow-right':  'brushArrowR',
};

/** Action id → its human label as shown in Settings → Keys, so the note tooltip
 *  can name the exact row to rebind. Derived from the same source the Keys
 *  settings tab renders, so the names always match. */
const ACTION_LABEL: Partial<Record<FlowAction, string>> = Object.fromEntries(
  KEYBIND_SECTIONS.flatMap(sec => sec.items.map(i => [i.action, i.label] as const)),
);

/** Tooltip content for a note brush: its description + the current shortcut, and
 *  a note pointing at where to rebind it (the tooltip itself isn't clickable). */
function noteBrushTip(title: string, body: string, action: FlowAction, combo: string): React.ReactNode {
  return tip({
    title,
    body,
    shortcut: prettyCombo(combo || '—'),
    shortcutLabel: 'Shortcut',
    note: `To change this shortcut, go to Settings → Keys → “${ACTION_LABEL[action] ?? action}”.`,
  });
}

/** Play Test HUD — hits / misses / accuracy, pinned under the perf pill. */
const playTestHudStyle: React.CSSProperties = {
  position: 'absolute',
  top: 52,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 13,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '6px 14px',
  borderRadius: 999,
  fontFamily: font.sans,
  fontSize: 11,
  color: color.ink,
  background: 'oklch(0.16 calc(0.03 * var(--tint-c)) var(--tint-h) / 0.86)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.45)',
  boxShadow: '0 6px 20px oklch(0 0 0 / 0.45)',
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
};

const playTestStatStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 11,
  fontWeight: 700,
};

/** ⚙ button in the Minimap panel header — opens the Minimap Options window
 *  (camera pose + zoom). Small and square so it sits inside the header row. */
const minimapGearBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  padding: 0,
  borderRadius: 7,
  cursor: 'pointer',
  color: color.accentSoft,
  background: 'oklch(0.20 calc(0.028 * var(--tint-c)) var(--tint-h) / 0.55)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
  transition: 'all 0.15s ease',
};


// 0..1 energy from chart notes near the playhead — drives the shared
// visualizer's "React to: Notes" mode. Pulses on a note, decays over ~1/4 beat.
function getFlowNoteEnergy(): number {
  const st = useFlowStore.getState();
  const chart = st.chart;
  if (!chart || !chart.notes.length) return 0;
  const beat = ((st.currentTime - chart.offset_seconds) * chart.bpm) / 60;
  let best = Infinity;
  for (const n of chart.notes) {
    const d = Math.abs(n.time_beats - beat);
    if (d < best) best = d;
    if (best < 0.01) break;
  }
  return Math.max(0, 1 - best / 0.25);
}

export default function FlowApp() {
  const setActiveView = useEditorStore(s => s.setActiveView);

  const chart = useFlowStore(s => s.chart);
  const setChart = useFlowStore(s => s.setChart);
  const playing = useFlowStore(s => s.playing);
  const setPlaying = useFlowStore(s => s.setPlaying);
  const liveMapping = useFlowStore(s => s.liveMapping);
  // Play Test: the lane keys hit notes instead of editing. Shift+Space =
  // practice (no score, free scrubbing); Ctrl+Shift+Space = scored run.
  const playTest = useFlowStore(s => s.playTest);
  const playTestScored = useFlowStore(s => s.playTestScored);
  const playTestStats = useFlowStore(s => s.playTestStats);
  const currentTime = useFlowStore(s => s.currentTime);
  const duration = useFlowStore(s => s.duration);
  const playbackSpeed = useFlowStore(s => s.playbackSpeed);
  const setPlaybackSpeed = useFlowStore(s => s.setPlaybackSpeed);
  const seek = useFlowStore(s => s.seek);
  const snapDivision = useFlowStore(s => s.snapDivision);
  const setSnapDivision = useFlowStore(s => s.setSnapDivision);
  const flowNjs = useFlowStore(s => s.flowNjs);
  const setFlowNjs = useFlowStore(s => s.setFlowNjs);
  const activeDifficulty = useFlowStore(s => s.activeDifficulty);
  const setActiveDifficulty = useFlowStore(s => s.setActiveDifficulty);
  const addDifficulty = useFlowStore(s => s.addDifficulty);
  const settingsOpen = useFlowStore(s => s.settingsOpen);
  const setSettingsOpen = useFlowStore(s => s.setSettingsOpen);
  const activeLaneCount = useFlowStore(s => laneCountFor(s.chart, s.activeDifficulty));
  const setActiveLaneCount = useFlowStore(s => s.setActiveLaneCount);
  // Pending lane-count change awaiting the redistribution confirmation popup.
  const [laneSwitchTarget, setLaneSwitchTarget] = useState<FlowLaneCount | null>(null);
  const noteTypeBrush = useFlowStore(s => s.noteTypeBrush);
  const setNoteTypeBrush = useFlowStore(s => s.setNoteTypeBrush);
  const noteMirror = useFlowStore(s => s.noteMirror);
  const setNoteMirror = useFlowStore(s => s.setNoteMirror);
  const keybindings = useFlowStore(s => s.keybindings);  // for the note tooltips' shortcut display

  // One-time migration: the Visualizer panel moved from the LEFT sidebar to the
  // RIGHT (below the Minimap). The panel-layout store persists placement by
  // label and ignores re-registration, so older sessions still have it pinned
  // left — move it to the right (just after the Minimap) so ordering + drag
  // behave correctly. Runs once; no-ops once it's already on the right.
  useEffect(() => {
    const pl = usePanelLayout.getState();
    const viz = pl.get('flow', 'Visualizer');
    if (viz && viz.sidebar === 'left') {
      pl.movePanel('flow', 'Visualizer', 'right', 1); // index 1 = right below the Minimap
    }
  }, []);
  const clearChart = useFlowStore(s => s.clearChart);
  const audioUrl = useFlowStore(s => s.audioUrl);
  const audioName = useFlowStore(s => s.audioName);
  const songName = useFlowStore(s => s.songName);
  const songAuthor = useFlowStore(s => s.songAuthor);
  const coverUrl = useFlowStore(s => s.coverUrl);
  const musicVolume = useFlowStore(s => s.musicVolume);
  const setMusicVolume = useFlowStore(s => s.setMusicVolume);
  const sfxVolume = useFlowStore(s => s.sfxVolume);
  const setSfxVolume = useFlowStore(s => s.setSfxVolume);
  const syncOffsetMs = useFlowStore(s => s.syncOffsetMs);
  const setSyncOffsetMs = useFlowStore(s => s.setSyncOffsetMs);
  // Transient "can't place in the offset zone" message (flashed by FlowScene).
  const offsetWarnAt = useFlowStore(s => s.offsetWarnAt);
  const [showOffsetWarn, setShowOffsetWarn] = React.useState(false);
  useEffect(() => {
    if (!offsetWarnAt) return;
    setShowOffsetWarn(true);
    const id = setTimeout(() => setShowOffsetWarn(false), 2200);
    return () => clearTimeout(id);
  }, [offsetWarnAt]);
  // Same, for an edit blocked by the edit-window lock or by Play Test.
  const editBlockWarnAt = useFlowStore(s => s.editBlockWarnAt);
  const [showEditBlockWarn, setShowEditBlockWarn] = React.useState(false);
  useEffect(() => {
    if (!editBlockWarnAt) return;
    setShowEditBlockWarn(true);
    const id = setTimeout(() => setShowEditBlockWarn(false), 2200);
    return () => clearTimeout(id);
  }, [editBlockWarnAt]);
  const cameraView = useFlowStore(s => s.cameraView);
  const setCameraView = useFlowStore(s => s.setCameraView);
  const customCameraPosition = useFlowStore(s => s.customCameraPosition);
  const requestSaveCustomCamera = useFlowStore(s => s.requestSaveCustomCamera);
  const clearCustomCamera = useFlowStore(s => s.clearCustomCamera);
  const audioLoading = useFlowStore(s => s.audioLoading);
  const audioError = useFlowStore(s => s.audioError);
  // Edit-map-info dialog visibility lives in the store so FlowScene's
  // SongInfoBanner can pop the dialog without prop drilling.
  const editMapInfoOpen = useFlowStore(s => s.editMapInfoOpen);
  const setEditMapInfoOpen = useFlowStore(s => s.setEditMapInfoOpen);

  const iface    = useFlowStore(s => s.flowInterfaceSettings);
  const setIface = useFlowStore(s => s.setFlowInterfaceSettings);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const chartInputRef = useRef<HTMLInputElement>(null);
  // Scroll containers for the floating jump-to-top/bottom arrows.
  const leftBarRef = useRef<HTMLDivElement>(null);
  const rightBarRef = useRef<HTMLDivElement>(null);
  const [chartStatus, setChartStatus] = React.useState<string | null>(null);
  const [chartError, setChartError] = React.useState<string | null>(null);
  // When a server save fails, we show a prompt offering an optional local
  // download backup (instead of silently downloading to the user's disk).
  const [serverDownPrompt, setServerDownPrompt] = React.useState(false);
  // True while a file is being dragged over the editor stage (drop-to-open).
  const [chartDragActive, setChartDragActive] = React.useState(false);
  // Name of the connected local folder (File System Access API), or null.
  // When set, Save writes the .flow straight into it and Open lists its maps.
  const [localFolder, setLocalFolder] = React.useState<string | null>(null);

  // Re-attach a previously-connected folder on mount and keep the name in
  // sync with the fsAccess module (connect/disconnect from the dialog).
  React.useEffect(() => {
    if (!isFsAccessSupported()) return;
    void restoreFolder().then(() => setLocalFolder(connectedFolderName()));
    return subscribeFolder(() => setLocalFolder(connectedFolderName()));
  }, []);
  /** Name this chart was last saved as. null = never saved, so the next
   *  Save click should prompt for a name via SaveAsDialog.
   *  Persisted to localStorage so browser refresh doesn't lose it. */
  const [savedAsName, _setSavedAsName] = React.useState<string | null>(() => {
    try { return localStorage.getItem('synapse:flow:savedAsName') || null; } catch { return null; }
  });
  const setSavedAsName = React.useCallback((name: string | null) => {
    _setSavedAsName(name);
    try {
      if (name) localStorage.setItem('synapse:flow:savedAsName', name);
      else localStorage.removeItem('synapse:flow:savedAsName');
    } catch { /* quota / private mode */ }
  }, []);
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
  const [loadDialogOpen, setLoadDialogOpen] = React.useState(false);
  const [newMapDialogOpen, setNewMapDialogOpen] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);

  // ── Sync debug console (toggle with Ctrl+Shift+D or the transport button) ──
  // Lives in the store so the transport panel's debug button and this keyboard
  // shortcut share one source of truth.
  const debugSync = useFlowStore(s => s.debugSync);
  const setDebugSync = useFlowStore(s => s.setDebugSync);
  const [debugLines, setDebugLines] = React.useState<string[]>([]);
  const [debugCopied, setDebugCopied] = React.useState(false);
  // Headline sync metrics for the on-screen panel (updated by the sampler).
  const [debugStats, setDebugStats] = React.useState<{ outLatMs: number; heardDeltaMs: number; cumRate: number } | null>(null);
  // Full sample buffer (oldest→newest) for the Copy button; the on-screen list
  // only shows the most recent few.
  const debugBufferRef = useRef<string[]>([]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'd' || e.key === 'D')) {
        e.preventDefault();
        useFlowStore.getState().setDebugSync(!useFlowStore.getState().debugSync);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);


  // Decode the track into the Web Audio engine and publish its exact duration.
  // The engine (not an <audio> element) is the master clock — see flowAudioEngine.
  useEffect(() => {
    if (!audioUrl) { engine.reset(); return; }
    let alive = true;
    engine.pause();
    engine.loadAudioBuffer(audioUrl)
      .then(dur => { if (alive && dur > 0) useFlowStore.getState().setDuration(dur); })
      .catch(err => console.warn('[flow] audio decode failed:', err));
    return () => { alive = false; };
  }, [audioUrl]);

  // Mirror playback speed + music volume into the engine.
  useEffect(() => { engine.setRate(playbackSpeed); }, [playbackSpeed]);
  useEffect(() => { engine.setVolume(musicVolume); }, [musicVolume]);

  // Mirror sfxVolume → tickSound module-level volume so scrub / step / hit
  // ticks respect the slider immediately.
  useEffect(() => {
    setTickVolume(sfxVolume);          // SR-style scrub/step ticks
    setFlowAudioVolume(sfxVolume);     // per-lane note HIT sounds + scroll tick
  }, [sfxVolume]);

  // Mirror the SFX preferences (scroll voice + volumes + lane mode) into the
  // sound module so changes in Settings take effect immediately.
  const sfxSettings = useFlowStore(s => s.sfxSettings);
  useEffect(() => {
    setScrollSound(sfxSettings.scrollSound);
    setScrollVolume(sfxSettings.scrollVolume);
    setNoteSound(sfxSettings.noteSound ?? 'tone');
    setNoteVolume(sfxSettings.noteVolume);
    setLaneSoundUniform(sfxSettings.laneSoundUniform);
    setPlaceSound(sfxSettings.placeSound);
    setDeleteSound(sfxSettings.deleteSound);
    setPlaceVolume(sfxSettings.placeVolume);
    setDeleteVolume(sfxSettings.deleteVolume);
  }, [sfxSettings]);

  // Seeks (wheel-scrub, timeline click, transport) go through the engine so the
  // real playhead moves even mid-playback. Natural end → stop playing.
  useEffect(() => {
    const register = useFlowStore.getState().registerAudioSeek;
    register((t) => {
      probe('seek', { t, pos: engine.getPosition() });
      // Drop stale live-hit suppression so a seek-back replays notes normally.
      liveHitSuppress.clear();
      engine.seek(t);
    });
    engine.setOnEnded(() => useFlowStore.getState().setPlaying(false));
    return () => { register(null); engine.setOnEnded(null); };
  }, []);

  useEffect(() => {
    if (playing) {
      const from = useFlowStore.getState().currentTime;
      engine.play(from);
      probe('play', { from, pos: engine.getPosition() });
    } else {
      engine.pause();
      probe('pause', { pos: engine.getPosition() });
      // On pause, optionally snap the playhead to the nearest snap-division
      // line so it lands on the grid instead of wherever the audio stopped.
      const st = useFlowStore.getState();
      if (st.flowInterfaceSettings.snapOnPause) {
        const stepBeats = 4 / Math.max(1, st.snapDivision);
        const snappedBeat = Math.max(0, Math.round(st.currentBeat / stepBeats) * stepBeats);
        const snapTime = (snappedBeat / Math.max(1, st.chart.bpm)) * 60 + st.chart.offset_seconds;
        st.seek(snapTime);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // Mirror the Sync debug console (Ctrl+Shift+D) into the timing probe, so the
  // same toggle that opens the on-screen sampler also streams sound-trigger /
  // action lines (hit drift, metronome lead, beat drift) to the console.
  useEffect(() => { setSyncProbeEnabled(debugSync); }, [debugSync]);

  // rAF loop — the visual clock follows the AUDIO DSP clock via
  // engine.getPosition() (AudioContext.currentTime), the rhythm-game standard.
  // With no track loaded, fall back to wall-clock so the editor still scrolls.
  const rafRef = useRef<number>(0);
  const lastTsRef = useRef<number>(0);
  useEffect(() => {
    if (!playing) { lastTsRef.current = 0; return; }
    const tick = (ts: number) => {
      const state = useFlowStore.getState();
      if (engine.hasBuffer()) {
        // Loop region (right-drag): once playback passes the loop's end beat,
        // wrap back to its start so the section repeats. A SCORED run is a
        // straight play-through — it ignores the loop entirely (otherwise the
        // run could never reach the end). Practice keeps looping, and each wrap
        // re-arms the notes in the loop so you can drill the same bars.
        const loop = state.playTestScored ? null : state.loopRegion;
        if (loop) {
          const spb = 60 / Math.max(1, state.chart.bpm);
          const loopEndSec = state.chart.offset_seconds + loop.endBeat * spb;
          if (engine.getPosition() >= loopEndSec) {
            engine.seek(state.chart.offset_seconds + loop.startBeat * spb);
            if (state.playTest) resetPlayTestRun();
          }
        }
        // Drive the visual playhead from the HEARD position (auto-compensated
        // for hardware output latency via getOutputTimestamp / outputLatency),
        // then apply the manual syncOffsetMs as a residual fine-tune.
        state.setCurrentTime(Math.max(0, engine.getHeardPosition() - state.syncOffsetMs / 1000));
        if (!engine.isPlaying()) { state.setPlaying(false); return; }
      } else {
        if (!lastTsRef.current) lastTsRef.current = ts;
        const dt = (ts - lastTsRef.current) / 1000;
        lastTsRef.current = ts;
        const nextT = state.currentTime + dt * state.playbackSpeed;
        if (nextT >= state.duration) {
          state.setPlaying(false);
          state.setCurrentTime(state.duration);
          return;
        }
        state.setCurrentTime(nextT);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, audioUrl]);

  // ── Note hit SFX — look-ahead scheduler (sample-accurate) ──────────────────
  // The hit sounds used to fire reactively when a note crossed the playhead in
  // the render loop, which lagged the beat by ~20–40ms with frame jitter (see
  // the sync probe). Instead we poll ahead and SCHEDULE each upcoming note's
  // hit at its EXACT AudioContext time (getCtxTimeForSong), the rhythm-game
  // standard — the same look-ahead the metronome uses. Only runs with a real
  // audio buffer (the audio DSP clock); the no-audio editor stays silent.
  useEffect(() => {
    // Play Test mutes the automatic hits: the only lane sounds you hear during
    // a run are the ones YOU land.
    if (!playing || playTest || !engine.hasBuffer()) return;
    const LOOKAHEAD = 0.12;   // seconds of notes to commit in advance
    const POLL_MS = 25;
    let scheduledUntil = -1;  // song time (s) through which hits are scheduled
    let lastPos = -1;         // detect seeks / loops to re-sync

    const id = window.setInterval(() => {
      const st = useFlowStore.getState();
      if (!st.playing || !engine.isPlaying()) { scheduledUntil = -1; lastPos = -1; return; }
      const pos = engine.getPosition();
      // Playhead jumped (seek/loop) → re-anchor so we don't replay or skip.
      if (lastPos >= 0 && Math.abs(pos - lastPos) > POLL_MS / 1000 * st.playbackSpeed + 0.25) {
        scheduledUntil = -1;
      }
      lastPos = pos;
      if (scheduledUntil < 0) scheduledUntil = pos;

      const horizon = pos + LOOKAHEAD;
      const bpm = Math.max(1, st.chart.bpm);
      const spb = 60 / bpm;
      const offs = st.chart.offset_seconds;
      // Collect the hits landing in (scheduledUntil, horizon] first, then
      // COALESCE simultaneous ones — two notes on the same beat used to fire
      // two voices into the same instant, and identical sounds summing is a
      // doubling in amplitude (user report: "suena muy alto"). One instant =
      // one hit; the lowest lane wins the pitch so a chord is deterministic.
      // We scan every note (not break-on-sort) because a trail's CLOSING note
      // fires at its END beat, which is later than its (sort-key) head beat.
      const pending = new Map<number, { time: number; lane: number; beat: number; src: string }>();
      const queue = (time: number, lane: number, beat: number, src: string) => {
        const key = Math.round(time * 1000);          // 1ms buckets
        const prev = pending.get(key);
        if (!prev || lane < prev.lane) pending.set(key, { time, lane, beat, src });
      };
      for (const note of st.chart.notes) {
        // HEAD hit.
        const headTime = offs + note.time_beats * spb;
        if (headTime > scheduledUntil && headTime <= horizon) {
          // Live-placed notes already sounded on key-press → don't double them.
          if (liveHitSuppress.has(note.id)) { liveHitSuppress.delete(note.id); }
          else queue(headTime, note.lane, note.time_beats, 'sched');
        }
        // TAIL hit — the trail's closing note crossing the strike line.
        const isTrail = (note.duration_beats && note.duration_beats > 0)
          || (note.waypoints && note.waypoints.length >= 2);
        if (isTrail) {
          const endBeats = note.time_beats + (note.duration_beats ?? 0);
          const endTime = offs + endBeats * spb;
          if (endTime > scheduledUntil && endTime <= horizon) {
            const endLane = (note.waypoints && note.waypoints.length >= 2)
              ? note.waypoints[note.waypoints.length - 1].lane
              : note.lane;
            queue(endTime, endLane, endBeats, 'sched-tail');
          }
        }
      }
      for (const h of pending.values()) {
        scheduleLaneHitAt(h.lane, engine.getCtxTimeForSong(h.time), { beat: h.beat, src: h.src });
      }
      scheduledUntil = horizon;
    }, POLL_MS);

    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, playTest, audioUrl]);

  // ── Sync debug sampler ────────────────────────────────────────────────
  // While the debug console is on AND playing, log a line every 0.5s with the
  // raw measurements needed to diagnose A/V sync: how fast audio.currentTime
  // advances vs real wall-clock time (rate; should be 1.000x), the visual
  // clock, the beat, durations, and playbackRate. Copy these from the browser
  // console (or the on-screen panel) and paste them back.
  useEffect(() => {
    if (!debugSync || !playing) return;
    // Baselines for the cumulative rate. They're re-anchored on the FIRST tick
    // (below) so the startup transient — source (re)start after a play/seek,
    // before the audio clock has settled — doesn't permanently drag cum down.
    let wall0  = performance.now();
    let audio0 = engine.getPosition();
    let lastWall = wall0;
    let lastAudio = audio0;
    let based = false;
    const startLine = `── start ──  audio0=${audio0.toFixed(3)}  storeDur=${useFlowStore.getState().duration.toFixed(2)}  engDur=${engine.getDuration().toFixed(2)}  bpm=${useFlowStore.getState().chart.bpm}  offset=${useFlowStore.getState().chart.offset_seconds}`;
    debugBufferRef.current.push(startLine);
    console.log('[flow-sync]', startLine);
    const id = window.setInterval(() => {
      const st = useFlowStore.getState();
      const nowWall  = performance.now();
      const nowAudio = engine.getPosition(); // audio DSP clock position
      // Re-anchor on the first tick (~0.5s in), once playback has stabilised.
      if (!based) {
        based = true;
        wall0 = nowWall; audio0 = nowAudio;
        lastWall = nowWall; lastAudio = nowAudio;
        return;
      }
      const dWall = (nowWall - lastWall) / 1000;
      const instRate = dWall > 0 ? (nowAudio - lastAudio) / dWall : 0;
      const cumWall  = (nowWall - wall0) / 1000;
      const cumRate  = cumWall > 0 ? (nowAudio - audio0) / cumWall : 0;
      lastWall = nowWall; lastAudio = nowAudio;
      const bpm = st.chart.bpm;
      const beat = ((st.currentTime - st.chart.offset_seconds) / 60) * bpm;
      const heardNow = engine.getHeardPosition();
      const outLatMs = engine.getOutputLatency() * 1000;
      const heardDeltaMs = (heardNow - nowAudio) * 1000; // negative = heard lags the DSP clock
      setDebugStats({ outLatMs, heardDeltaMs, cumRate });
      const line =
        `wall=${cumWall.toFixed(2)}s  audio=${nowAudio.toFixed(3)}  ` +
        `rate[inst=${instRate.toFixed(3)} cum=${cumRate.toFixed(3)}]  ` +
        `vis=${st.currentTime.toFixed(3)}  heard=${heardNow.toFixed(3)}  ` +
        `outLat=${outLatMs.toFixed(1)}ms  beat=${beat.toFixed(2)}  ` +
        `pbRate=${st.playbackSpeed}  bpm=${bpm}  ` +
        `dur[store=${st.duration.toFixed(2)} eng=${engine.getDuration().toFixed(2)}]`;
      console.log('[flow-sync]', line);
      debugBufferRef.current.push(line);
      if (debugBufferRef.current.length > 4000) debugBufferRef.current.shift();
      setDebugLines(prev => [line, ...prev].slice(0, 12));
    }, 500);
    return () => window.clearInterval(id);
  }, [debugSync, playing]);

  const handleFilePicked = useCallback(async (file: File | null | undefined) => {
    if (!file) return;
    try { await loadAudioFile(file); } catch { /* surfaced via store */ }
  }, []);

  // ── Draft autosave: restore on mount, subscribe to changes ────────────
  // Runs once when the editor first shows up — picks up any chart-in-progress
  // from localStorage and starts auto-persisting future edits.
  useEffect(() => {
    restoreDraftIfAny();
    const detach = attachDraftAutosave();
    return () => detach();
  }, []);

  // Keyboard shortcuts are handled by a single config-driven dispatcher
  // defined below (after handleSave), so all bindings are user-rebindable.

  // ── Live Mapping ───────────────────────────────────────────────────────
  // While the song plays and Live Mapping is on, A/S/D/F drop notes on lanes
  // 0..3 (left→right) at the playhead. A QUICK tap = a normal note; only a
  // press HELD longer than TRAIL_HOLD_MS becomes a trail, so fast taps don't
  // accidentally turn into trails. A trail is measured from the beat where the
  // key was first pressed (the hold start) to the beat at release. Positions
  // snap to the active snap grid.
  useEffect(() => {
    if (!liveMapping || playTest) return;
    // A/S/D/F (+G when the difficulty runs 5 lanes — extra keys past the active
    // lane count are simply ignored by the guard below). Rebindable in
    // Settings → Keys → "Lane Keys", shared with Play Test.
    const KEY_LANE = laneKeyMap(keybindings);
    // Minimum real hold time (ms) before a press counts as a trail. Below this
    // it's a plain tap, no matter how the snapped beats fall.
    const TRAIL_HOLD_MS = 1000;
    const pressStart: Record<number, number | null> = { 0: null, 1: null, 2: null, 3: null };
    const pressMs: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0 };
    // Id of the note dropped on key-DOWN, so key-UP can stretch it into a trail.
    const liveNoteId: Record<number, string | null> = { 0: null, 1: null, 2: null, 3: null };
    // Live Mapping snaps to a fixed, INVISIBLE 1/512 grid (not the visible snap
    // setting), so live-played notes land almost exactly where the user pressed
    // regardless of which snap level is selected for manual editing.
    const LIVE_SNAP = 512;
    const snap = (beat: number) => {
      const step = 4 / LIVE_SNAP;
      return Math.max(0, Math.round(beat / step) * step);
    };
    const isTyping = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onDown = (e: KeyboardEvent) => {
      const lane = KEY_LANE[e.key.toLowerCase()] as 0 | 1 | 2 | 3 | 4 | undefined;
      if (lane === undefined || isTyping(e.target)) return;
      e.preventDefault();
      if (e.repeat) return;                    // ignore auto-repeat while held
      const st = useFlowStore.getState();
      // Ignore keys past the active lane count (G only works on 5-lane diffs).
      if (lane >= laneCountFor(st.chart, st.activeDifficulty)) return;
      liveLaneHeld[lane] = true;               // light the lane keypad while held
      if (!st.playing) return;                 // record only during playback
      const start = snap(st.currentBeat);      // beat where the hold began
      pressStart[lane] = start;
      pressMs[lane] = performance.now();       // wall-clock to measure hold time
      // Drop the note IMMEDIATELY on press so it appears with zero perceived
      // latency. It starts as a tap; if the key is held past TRAIL_HOLD_MS the
      // key-up handler stretches this same note into a trail.
      liveNoteId[lane] = st.liveAddNote(lane, start, 0);
      playLaneHit(lane, { beat: start, src: 'live-down' }); // hit sound on press
    };
    const onUp = (e: KeyboardEvent) => {
      const lane = KEY_LANE[e.key.toLowerCase()] as 0 | 1 | 2 | 3 | 4 | undefined;
      if (lane === undefined) return;
      liveLaneHeld[lane] = false;              // key released → keypad off
      const start = pressStart[lane];
      const noteId = liveNoteId[lane];
      pressStart[lane] = null;
      liveNoteId[lane] = null;
      if (start == null || noteId == null) return;
      const heldMs = performance.now() - pressMs[lane];
      if (heldMs < TRAIL_HOLD_MS) return;      // quick tap → leave the note as-is
      // Held long enough → stretch the already-placed note into a trail,
      // counted from the press beat to the release beat.
      const st = useFlowStore.getState();
      const end = snap(st.currentBeat);
      const dur = Math.max(0, end - start);
      if (dur > 0) {
        st.liveExtendNote(noteId, dur);
        playLaneHit(lane, { beat: end, src: 'live-up' }); // trail also sounds on release
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      liveLaneHeld.fill(false);
    };
  }, [liveMapping, playTest, keybindings]);

  // ── Play Test ──────────────────────────────────────────────────────────
  // Shift+Space plays the map as a player would: the lane keys HIT the notes
  // instead of placing them. A press within PLAY_TEST_HIT_MS of a note in that
  // lane counts as a hit (and makes the sound); notes whose window expires
  // unhit — and presses with nothing to hit — count as misses. The look-ahead
  // hit-sound scheduler is muted for the run, so what you hear is what YOU hit.
  useEffect(() => {
    if (!playTest) return;
    const KEY_LANE = laneKeyMap(keybindings);
    const WINDOW_S = PLAY_TEST_HIT_MS / 1000;
    const isTyping = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    /** Song time (s) a note's head crosses the strike line. */
    const headTimeOf = (n: { time_beats: number }, offs: number, spb: number) => offs + n.time_beats * spb;

    const onDown = (e: KeyboardEvent) => {
      const lane = KEY_LANE[e.key.toLowerCase()];
      if (lane === undefined || isTyping(e.target) || e.repeat) return;
      e.preventDefault();
      const st = useFlowStore.getState();
      if (lane >= laneCountFor(st.chart, st.activeDifficulty)) return;
      liveLaneHeld[lane] = true;                     // light the keypad (it lifts)
      const pos = engine.hasBuffer() ? engine.getPosition() : st.currentTime;
      const spb = 60 / Math.max(1, st.chart.bpm);
      const offs = st.chart.offset_seconds;
      // Closest un-judged note in this lane inside the timing window.
      let best: { note: FlowNote; d: number } | null = null;
      for (const n of st.chart.notes) {
        if (n.lane !== lane || playTestJudgedIds.has(n.id)) continue;
        const d = Math.abs(headTimeOf(n, offs, spb) - pos);
        if (d <= WINDOW_S && (!best || d < best.d)) best = { note: n, d };
      }
      if (best) {
        const n = best.note;
        playTestJudgedIds.add(n.id);
        playTestSparkQueue.push({ lane, strong: false });   // sparks ONLY on a hit
        playLaneHit(lane, { beat: n.time_beats, src: 'play-test' });
        if (st.playTestScored) st.bumpPlayTest('hit');      // practice keeps no score
        // Trails: remember the hold so the scene can loop particles at the
        // strike line until the rail runs out (or the key comes up).
        const endBeat = n.waypoints && n.waypoints.length >= 2
          ? n.waypoints[n.waypoints.length - 1].time_beats
          : n.time_beats + (n.duration_beats ?? 0);
        const isTrail = endBeat > n.time_beats;
        playTestHolds[lane] = isTrail ? { noteId: n.id, endBeat } : null;
        // A hit TAP disappears; a trail stays on screen — you're still riding
        // it, and the rail scrolls away on its own once it's done.
        if (!isTrail) playTestHitIds.add(n.id);
      } else if (st.playTestScored) {
        st.bumpPlayTest('miss');                     // pressed with nothing there
      }
    };
    const onUp = (e: KeyboardEvent) => {
      const lane = KEY_LANE[e.key.toLowerCase()];
      if (lane === undefined) return;
      liveLaneHeld[lane] = false;
      playTestHolds[lane] = null;                    // let go → particles stop
    };

    // Sweep: notes whose hit window has fully passed without a press are
    // misses. Scored runs only — practice doesn't keep score, and its
    // un-judged notes simply fly past (they're re-armed by scrubbing back).
    let judgedThrough = -Infinity;                   // song time (s) already judged
    const sweep = window.setInterval(() => {
      const st = useFlowStore.getState();
      if (!st.playing || !st.playTestScored) return;
      const pos = (engine.hasBuffer() ? engine.getPosition() : st.currentTime) - WINDOW_S;
      const spb = 60 / Math.max(1, st.chart.bpm);
      const offs = st.chart.offset_seconds;
      if (judgedThrough === -Infinity) judgedThrough = pos;
      let misses = 0;
      for (const n of st.chart.notes) {
        const t = headTimeOf(n, offs, spb);
        if (t > judgedThrough && t <= pos && !playTestJudgedIds.has(n.id)) {
          // Judged as a MISS: it is not added to playTestHitIds, so the note
          // keeps its place and flies on past the strike line.
          playTestJudgedIds.add(n.id);
          misses++;
        }
      }
      judgedThrough = pos;
      for (let i = 0; i < misses; i++) st.bumpPlayTest('miss');
    }, 60);

    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.clearInterval(sweep);
      liveLaneHeld.fill(false);
      playTestHolds.fill(null);
    };
  }, [playTest, keybindings]);

  /** Perform the actual save with a known name. Used by both the first-
   *  time SaveAsDialog confirm and the silent re-save on subsequent clicks. */
  const performSave = useCallback(async (nameOverride?: string) => {
    setChartError(null);
    setServerDownPrompt(false);

    // Local folder takes priority: when one is connected, write the .flow
    // straight into it (no server round-trip, no download). The Save click is
    // a user gesture, so the permission prompt is allowed here if needed.
    if (isFsAccessSupported() && connectedFolderName()) {
      try {
        const { blob, filename } = await buildFlowBundle(nameOverride);
        await writeFolderChart(filename, blob);
        setSavedAsName(filename.replace(/\.flow$/, ''));
        setChartStatus(`Saved to ${connectedFolderName()}/${filename}`);
        setTimeout(() => setChartStatus(null), 4500);
      } catch (e: any) {
        setChartError(e?.message || 'Could not write to the local folder.');
        setServerDownPrompt(true);
      }
      return;
    }

    try {
      const { name } = await saveChartToServer(nameOverride);
      setSavedAsName(name.replace(/\.flow$/, ''));
      setChartStatus('Map saved');
      setTimeout(() => setChartStatus(null), 4500);
    } catch (e: any) {
      // The server save failed (backend offline / error). Don't silently
      // download a file to the user's disk — surface a clear error and let
      // them choose to download a local backup via the prompt banner.
      setChartError(
        e?.message?.includes('Failed to fetch') || e?.name === 'TypeError'
          ? "Couldn't reach the server. Start the backend and try again."
          : (e?.message || 'Save to server failed.'),
      );
      setServerDownPrompt(true);
    }
  }, []);

  /** Explicit local-backup download offered when a server save fails. */
  const downloadLocalBackup = useCallback(async () => {
    try {
      const filename = await downloadCurrentChart();
      setServerDownPrompt(false);
      setChartError(null);
      setChartStatus(`Downloaded ${filename} as a local backup`);
      setTimeout(() => setChartStatus(null), 5500);
    } catch (e: any) {
      setChartError(e?.message || 'Download failed');
    }
  }, []);

  const handleSave = useCallback(() => {
    setChartError(null);
    if (chart.notes.length === 0) {
      setChartError('Nothing to save — chart is empty.');
      return;
    }
    // First save (or after Clear): prompt for a name. Subsequent saves
    // re-use the chosen name silently — the editor is already busy enough.
    if (!savedAsName) {
      setSaveDialogOpen(true);
      return;
    }
    void performSave(savedAsName);
  }, [chart.notes.length, savedAsName, performSave]);

  // ── Keyboard dispatcher ───────────────────────────────────────────────
  // Single window listener: matches the event against the user's rebindable
  // keybindings and runs the action. Bails while typing in a field. Esc stays
  // fixed (clear cut/selection). NOTE: Chrome reserves Ctrl+N for a new window
  // and may not let the page intercept it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const s = useFlowStore.getState();

      if (e.key === 'Escape') {
        // Esc is the universal "get me out" — leaving a Play Test run comes first.
        if (s.playTest) { e.preventDefault(); s.setPlaying(false); s.setPlayTest(false); return; }
        if (s.cutNoteIds.size > 0) { e.preventDefault(); s.clearCut(); return; }
        if (s.selection.noteIds.size > 0) { e.preventDefault(); s.clearSelection(); }
        return;
      }

      const action = matchFlowAction(e, s.keybindings);
      if (!action) return;
      // During a Play Test the keyboard belongs to the player: only the exits
      // (either Play Test toggle, play/pause) still fire. Lane keys are handled
      // by the Play Test effect; everything else would edit the chart mid-run.
      if (s.playTest && action !== 'playTest' && action !== 'playTestScored' && action !== 'playPause') return;
      switch (action) {
        case 'playPause':     e.preventDefault(); s.setPlaying(!s.playing); break;
        // Practice (Shift+Space): no score — play from wherever the playhead
        // is, scrub back/forward mid-run and keep playing.
        case 'playTest': {
          e.preventDefault();
          if (s.playTest) { s.setPlaying(false); s.setPlayTest(false); }
          else {
            s.clearSelection();
            s.setPlayTest(true, false);
            if (!s.playing) s.setPlaying(true);
          }
          break;
        }
        // Scored run (Ctrl+Shift+Space): a real play-through — restarts the
        // song from 0:00, counts hits/misses, and locks every way of seeking
        // until you exit.
        case 'playTestScored': {
          e.preventDefault();
          if (s.playTest) { s.setPlaying(false); s.setPlayTest(false); }
          else {
            s.clearSelection();
            s.seek(0);                    // before playTest flips on — allowed
            s.setPlayTest(true, true);
            s.setPlaying(true);
          }
          break;
        }
        case 'editWindow':    e.preventDefault(); s.setEditWindowEnabled(!s.editWindowEnabled); break;
        case 'playFromStart': e.preventDefault(); s.seek(0); s.setPlaying(true); break;
        // SR-parity timeline/snap keys. PAUSED ONLY: while the music runs these
        // physical keys belong to the lane keys (live mapping), so we bail and
        // let that handler take them.
        case 'stepForward':
        case 'stepBack': {
          if (s.playing) return;
          e.preventDefault();
          const step = 4 / Math.max(s.snapDivision, 1);
          const sign = action === 'stepForward' ? +1 : -1;
          const beat = Math.max(0, s.currentBeat + sign * step * (e.shiftKey ? 4 : 1));
          s.seek((beat * 60) / Math.max(1, s.chart.bpm) + s.chart.offset_seconds);
          break;
        }
        case 'snapFiner':
        case 'snapCoarser': {
          if (s.playing) return;
          e.preventDefault();
          const opts = SNAP_OPTIONS;
          const i = opts.indexOf(s.snapDivision);
          const next = action === 'snapFiner'
            ? opts[Math.min(opts.length - 1, (i === -1 ? 0 : i) + 1)]
            : opts[Math.max(0, (i === -1 ? 0 : i) - 1)];
          s.setSnapDivision(next);
          break;
        }
        case 'delete':        if (s.selection.noteIds.size === 0) return; e.preventDefault(); s.deleteSelection(); break;
        case 'copy':          if (s.selection.noteIds.size === 0) return; e.preventDefault(); s.copySelection(); break;
        case 'cut':           if (s.selection.noteIds.size === 0) return; e.preventDefault(); s.cutSelection(); break;
        case 'paste':         e.preventDefault(); s.pasteAtPlayhead(); break;
        case 'pasteMirror':   e.preventDefault(); s.pasteMirrorAtPlayhead(); break;
        case 'bookmarkToggle': e.preventDefault(); s.toggleBookmarkAtPlayhead(); break;
        case 'bookmarkNext':  e.preventDefault(); s.jumpBookmark(1); break;
        case 'bookmarkPrev':  e.preventDefault(); s.jumpBookmark(-1); break;
        case 'selectAll':     e.preventDefault(); s.selectAllNotes(); break;
        case 'deselect':      e.preventDefault(); if (s.cutNoteIds.size > 0) s.clearCut(); if (s.selection.noteIds.size > 0) s.clearSelection(); break;
        case 'undo':          if (!s.canUndo) return; e.preventDefault(); s.undo(); break;
        case 'redo':          if (!s.canRedo) return; e.preventDefault(); s.redo(); break;
        case 'nudgeLater':
        case 'nudgeEarlier': {
          if (s.selection.noteIds.size === 0) return;
          e.preventDefault();
          const step = 4 / Math.max(s.snapDivision, 1);
          const sign = action === 'nudgeLater' ? +1 : -1;
          s.shiftSelectionBy(sign * step * (e.shiftKey ? 4 : 1));
          break;
        }
        case 'nudgeLaneLeft':
        case 'nudgeLaneRight': {
          if (s.selection.noteIds.size === 0) return;
          e.preventDefault();
          s.shiftSelectionLane(action === 'nudgeLaneRight' ? +1 : -1);
          break;
        }
        case 'brushTap':      e.preventDefault(); s.setNoteTypeBrush('tap'); break;
        case 'brushArrowL':   e.preventDefault(); s.setNoteTypeBrush('arrow-left'); break;
        case 'brushArrowC':   e.preventDefault(); s.setNoteTypeBrush('arrow-center'); break;
        case 'brushArrowR':   e.preventDefault(); s.setNoteTypeBrush('arrow-right'); break;
        case 'brushTrail': {
          // T TOGGLES trail mode (user feedback: pressing T again exits back to
          // tap, matching how rail creation feels in the desktop editor).
          e.preventDefault();
          s.setNoteTypeBrush(s.noteTypeBrush === 'trail' ? 'tap' : 'trail');
          break;
        }
        case 'newMap':        e.preventDefault(); setNewMapDialogOpen(true); break;
        case 'open':          e.preventDefault(); setLoadDialogOpen(true); break;
        case 'save':          e.preventDefault(); handleSave(); break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleSave]);

  // Safety net: stop the browser from navigating away (and losing unsaved work)
  // when a dragged file is dropped anywhere outside the editor's drop zone.
  React.useEffect(() => {
    const hasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types || []).includes('Files');
    const prevent = (e: DragEvent) => { if (hasFiles(e)) e.preventDefault(); };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);


  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void document.documentElement.requestFullscreen?.();
  }, []);


  const handleLoadChartFile = useCallback(async (file: File | null | undefined) => {
    if (!file) return;
    setChartError(null);
    setChartStatus(null);
    try {
      // .synth (Synth Riders) maps go through the importer: the backend parses
      // the archive and the VR grid is quantized onto Flow's lanes.
      if (/\.synth$/i.test(file.name)) {
        setChartStatus('Importing .synth…');
        const title = await importSynthToFlow(file);
        setChartStatus(`Imported "${title}" from .synth`);
        setTimeout(() => setChartStatus(null), 5500);
        setSavedAsName(null);
        return;
      }
      const loaded = await loadChartFromFile(file);
      const diff = loaded.difficulties[0];
      const noteCount = diff.taps.length + diff.flicks.length + diff.rails.length;
      setChartStatus(`Loaded ${noteCount} notes`);
      setTimeout(() => setChartStatus(null), 4500);
      // Loaded from disk — clear the "last saved as" name so the next Save
      // prompts again (we don't want to silently overwrite a server chart
      // with whatever name happens to match).
      setSavedAsName(null);
    } catch (e: any) {
      if (e instanceof FlowChartLoadError) {
        setChartError(e.message);
      } else {
        setChartError(e?.message || 'Failed to load chart');
      }
    }
  }, []);

  /** Drop a .flow / .json chart / .synth map onto the editor stage to open it. */
  const handleChartDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setChartDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const ok = /\.(flow|json|synth)$/i.test(file.name)
      || file.type === 'application/json' || file.type === 'application/zip';
    if (!ok) {
      setChartError(`"${file.name}" isn't a .flow or .synth map.`);
      setTimeout(() => setChartError(null), 4500);
      return;
    }
    void handleLoadChartFile(file);
  }, [handleLoadChartFile]);

  /** Only treat a drag as map-open when it actually carries files. */
  const dragHasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types || []).includes('Files');

  /** Open a .flow map straight from the connected local folder. Keeps the
   *  filename as the "saved as" name so a re-save writes back to the same file. */
  const handlePickFromFolder = useCallback(async (name: string) => {
    setChartError(null);
    setChartStatus(null);
    setLoadDialogOpen(false);
    try {
      const file = await readFolderChart(name);
      const loaded = await loadChartFromFile(file);
      const diff = loaded.difficulties[0];
      const noteCount = diff.taps.length + diff.flicks.length + diff.rails.length;
      setChartStatus(`Loaded ${noteCount} notes`);
      setTimeout(() => setChartStatus(null), 4500);
      setSavedAsName(name.replace(/\.flow$/, ''));
    } catch (e: any) {
      setChartError(e instanceof FlowChartLoadError ? e.message : (e?.message || 'Failed to load chart'));
    }
  }, []);

  /** Connect (pick) a local folder for reading/writing maps. */
  const handleConnectFolder = useCallback(async () => {
    try {
      await pickFolder();
      // subscribeFolder() updates localFolder; nothing else to do here.
    } catch (e: any) {
      // AbortError = the user dismissed the picker; not an error worth showing.
      if (e?.name !== 'AbortError') setChartError(e?.message || 'Could not open folder picker.');
    }
  }, []);

  const handleClearChart = useCallback(() => {
    clearChart();
    setSavedAsName(null);
    setChartStatus(null);
    setChartError(null);
  }, [clearChart]);

  const bumpBpm = useCallback((delta: number) => {
    setChart({ ...chart, bpm: Math.max(40, Math.min(500, chart.bpm + delta)) });
  }, [chart, setChart]);

  const bumpOffset = useCallback((deltaSeconds: number) => {
    const next = Math.max(-10, Math.min(10, chart.offset_seconds + deltaSeconds));
    // Round to 3 decimal places so the readout stays clean.
    setChart({ ...chart, offset_seconds: Math.round(next * 1000) / 1000 });
  }, [chart, setChart]);

  // ── Auto BPM / Auto Offset ────────────────────────────────────────────
  // Re-detect from the currently loaded track. Auto BPM re-runs the backend
  // librosa analysis; Auto Offset scans client-side for the first audible
  // onset (skipping the leading silence / white noise at the song start).
  const [autoOffsetBusy, setAutoOffsetBusy] = React.useState(false);

  // ── Auto BPM (detect locally + verify online in one flow) ──────────────
  // A popup walks the stages: detecting → verifying online → (choose if they
  // differ) → done. The detected value is applied immediately so even if the
  // online check fails we keep a tempo.
  type BpmStage = null | 'detecting' | 'verifying' | 'choose' | 'done';
  const [bpmStage, setBpmStage]       = React.useState<BpmStage>(null);
  const [bpmDetected, setBpmDetected] = React.useState<number | null>(null);
  const [bpmOnline, setBpmOnline]     = React.useState<number | null>(null);
  const [bpmOnlineNote, setBpmOnlineNote] = React.useState('');
  const [bpmFinal, setBpmFinal]       = React.useState<number | null>(null);
  const [qrOpen, setQrOpen] = React.useState(false);
  const [bpmConfidence, setBpmConfidence] = React.useState<number | null>(null);
  const [bpmCandidates, setBpmCandidates] = React.useState<{ bpm: number; strength: number }[]>([]);
  // Set when detection and the online value were in a ½×/2× relationship and we
  // auto-corrected the octave (keeping our precise decimal). Shown in 'done'.
  const [bpmReconciled, setBpmReconciled] = React.useState<string>('');

  const applyBpm = useCallback((bpm: number) => {
    const v = Math.max(40, Math.min(500, Math.round(bpm * 100) / 100));
    setChart({ ...useFlowStore.getState().chart, bpm: v });
    return v;
  }, [setChart]);

  // Auto-detection rounds to the NEAREST WHOLE BPM (user feedback: decimal BPM
  // isn't standard practice for the game — 149.61 should land on 150, with the
  // candidate chips still offering the bracketing integers to pick by ear).
  // Manually-typed BPM (sidebar / New Map) still accepts decimals untouched.
  const snapNiceBpm = useCallback((bpm: number) => Math.round(bpm), []);

  // Nearby "round" tempos to offer as one-click fixes. Most tracks are integer
  // (some .5) BPM, and our real-audio estimate can sit ~0.5 off — so offering
  // BOTH bracketing integers (and the half) lets the user lock the exact round
  // tempo by ear/metronome (e.g. 149.61 → pick 149, not the rounded 150).
  const handleAutoBpm = useCallback(async () => {
    if (bpmStage === 'detecting' || bpmStage === 'verifying') return;
    setBpmDetected(null); setBpmOnline(null); setBpmFinal(null); setBpmOnlineNote('');
    setBpmConfidence(null); setBpmCandidates([]); setBpmReconciled('');

    // 1) Detect from the audio (precise BPM + octave confidence + ½×/2× cands).
    setBpmStage('detecting');
    let detected: number;
    try {
      const r = await detectAudioBpm();
      detected = applyBpm(snapNiceBpm(r.bpm));
      setBpmConfidence(r.confidence);
      setBpmCandidates(r.candidates);
    } catch (err) {
      console.warn('[flow] Auto BPM failed:', err);
      setBpmStage(null);
      return;
    }
    setBpmDetected(detected);

    // Online BPM verification removed — keep the detected (librosa) value.
    let online: number | null = null;

    if (online == null) {
      setBpmFinal(detected);
      setBpmStage('done');
      return;
    }
    setBpmOnline(online);

    // 3) Reconcile against the online value.
    //   a. Already equal (within ~2%) → done.
    //   b. They line up at an OCTAVE (½× / 2× / ⅓× / 3×) → it's an octave error,
    //      not a real disagreement: keep our PRECISE decimal but snap to the
    //      octave the online value confirms (e.g. detected 68.55 + online 137 →
    //      137.1). Auto-applied; no prompt needed.
    //   c. Genuine mismatch → let the user choose (with confidence + candidates).
    if (Math.abs(detected - online) <= Math.max(1, detected * 0.02)) {
      setBpmFinal(detected);
      setBpmStage('done');
      return;
    }

    const ratios = [2, 0.5, 3, 1 / 3];
    const tol = Math.max(1, online * 0.04); // 4% window for the octave match
    let octave: number | null = null;
    for (const r of ratios) {
      if (Math.abs(detected * r - online) <= tol) { octave = detected * r; break; }
    }
    if (octave != null) {
      const corrected = applyBpm(octave);
      setBpmFinal(corrected);
      setBpmReconciled(`Octave-corrected ${detected} → ${corrected} to match the online ${online} BPM.`);
      setBpmStage('done');
    } else {
      setBpmStage('choose');
    }
  }, [bpmStage, applyBpm, snapNiceBpm]);

  const chooseBpm = useCallback((bpm: number) => {
    setBpmFinal(applyBpm(bpm));
    setBpmStage('done');
  }, [applyBpm]);

  // Auto-dismiss the "done" confirmation after a moment.
  useEffect(() => {
    if (bpmStage !== 'done') return;
    // If the applied tempo is fractional, keep the popup open so the user can
    // fine-tune to a round value (most tracks are integer BPM). Integer results
    // auto-dismiss as before.
    if (bpmFinal != null && Math.abs(bpmFinal - Math.round(bpmFinal)) > 1e-6) return;
    const id = window.setTimeout(() => setBpmStage(null), bpmReconciled ? 4200 : 2200);
    return () => window.clearTimeout(id);
  }, [bpmStage, bpmReconciled, bpmFinal]);

  const handleAutoOffset = useCallback(async () => {
    if (autoOffsetBusy) return;
    setAutoOffsetBusy(true);
    try {
      const bpm = useFlowStore.getState().chart.bpm;
      let offset: number;
      try {
        // Prefer the BPM-aware, phase-locked offset so the grid lines up with
        // the tempo instead of the first audible onset.
        offset = await detectAudioBeatOffset(bpm);
      } catch {
        // Fallback: first-onset RMS scan (used if the backend is unavailable).
        offset = await detectAudioOffset();
      }
      const c = useFlowStore.getState().chart;
      setChart({ ...c, offset_seconds: Math.round(offset * 1000) / 1000 });
    } catch (err) {
      console.warn('[flow] Auto Offset failed:', err);
    } finally {
      setAutoOffsetBusy(false);
    }
  }, [autoOffsetBusy, setChart]);

  const snapOptions = [4, 8, 16, 32, 64, 128, 256, 512];
  const speedOptions = [0.25, 0.5, 1, 1.5, 2];
  const njsOptions = [0.1, 0.2, 0.3, 0.4, 0.5, 0.75, 1, 1.5, 2, 3, 4];


  return (
    <div style={{ ...shellStyle, gridTemplateColumns: `var(--sr-left-w, 230px) 1fr var(--sr-right-w, 245px)`, paddingBottom: 0 }}>
      {/* Full-screen background — the gradient, particle visual and imported
          .visualizer/.vizproject render FIXED behind everything (z-index -1), so
          the background fills the entire screen behind the floating panels (like
          the Synth VR editor) instead of being framed inside the centre column. */}
      <div style={{ position: 'fixed', inset: 0, zIndex: -1, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={bg3dLayerStyle} />
        {/* Shared visualizer: particle layer + loaded .visualizer scene, driven
            by the playhead and the live audio / notes (one module for all editors). */}
        <VisualizerBackground
          getTime={() => useFlowStore.getState().currentTime}
          getAnalyser={engine.getAnalyser}
          audioContext={getFlowAudioContext()}
          getNoteEnergy={getFlowNoteEnergy}
        />
      </div>
      <aside style={{ ...leftPanelStyle, overflowY: 'visible', gap: 0, padding: 0, marginBottom: 'var(--shell-pad-y, 16px)' }}>
        {/* Top header: circular Back + accent pill. Kept OUTSIDE the scroll
            element below so the scrollbar range and the edge fade leave the
            editor logo alone. */}
        <div style={{ flexShrink: 0, padding: '0 10px 0 11px' }}>
          <EditorBackHeader name="Flow" onBack={() => setActiveView('dashboard')} />
        </div>

        {/* Hidden file inputs — outside the scroller so the refs survive. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.ogg,.wav,.m4a,.flac"
          onChange={e => handleFilePicked(e.target.files?.[0])}
          style={{ display: 'none' }}
        />
        <input
          ref={chartInputRef}
          type="file"
          accept=".flow,application/json,.json,.synth"
          onChange={e => handleLoadChartFile(e.target.files?.[0])}
          style={{ display: 'none' }}
        />

      <div ref={leftBarRef} className="overlay-scroll" style={{ ...sidebarScrollStyle, padding: '0 10px 0 11px' }}>
        <ScrollArrows targetRef={leftBarRef} />
        <ScrollFade targetRef={leftBarRef} />
        <OverlayScrollbar targetRef={leftBarRef} side="left" rightInset={5} />

        <Group label="Controls">
          <ControlsPanel
            steppers={[
              { label: 'BPM', value: fmtBpm(chart.bpm), onDec: () => bumpBpm(-1), onInc: () => bumpBpm(1),
                onCommitValue: (raw) => { const v = parseFloat(raw); if (Number.isFinite(v)) setChart({ ...useFlowStore.getState().chart, bpm: Math.max(40, Math.min(500, v)) }); },
                editTitle: "Type exact BPM (decimals allowed) to match the song's tempo" },
              { label: 'Offset', value: `${(chart.offset_seconds * 1000).toFixed(0)} ms`, onDec: () => bumpOffset(-0.010), onInc: () => bumpOffset(+0.010),
                onCommitValue: (raw) => { const v = parseFloat(raw.replace(/ms/i, '').trim()); if (Number.isFinite(v)) { const next = Math.max(-10000, Math.min(10000, v)) / 1000; setChart({ ...useFlowStore.getState().chart, offset_seconds: Math.round(next * 1000) / 1000 }); } },
                editTitle: 'Type exact offset in milliseconds' },
              { label: 'Speed', value: `${playbackSpeed}x`,
                onDec: () => { const i = speedOptions.indexOf(playbackSpeed); setPlaybackSpeed(speedOptions[Math.max(0, i - 1)]); },
                onInc: () => { const i = speedOptions.indexOf(playbackSpeed); setPlaybackSpeed(speedOptions[Math.min(speedOptions.length - 1, i + 1)]); } },
              // Snap reads as a fraction of a BEAT (reviewer ask): stored
              // snapDivision is per-BAR (4 = one beat), so display /4 —
              // "1/1" steps a whole beat, "1/4" splits a beat in four.
              { label: 'Snap', value: `1/${snapDivision / 4}`,
                onDec: () => { const i = snapOptions.indexOf(snapDivision); setSnapDivision(snapOptions[Math.max(0, i - 1)]); },
                onInc: () => { const i = snapOptions.indexOf(snapDivision); setSnapDivision(snapOptions[Math.min(snapOptions.length - 1, i + 1)]); } },
              { label: 'NJS', value: `${flowNjs}x`,
                onDec: () => { const i = njsOptions.indexOf(flowNjs); setFlowNjs(njsOptions[Math.max(0, (i === -1 ? njsOptions.indexOf(1) : i) - 1)]); },
                onInc: () => { const i = njsOptions.indexOf(flowNjs); setFlowNjs(njsOptions[Math.min(njsOptions.length - 1, (i === -1 ? njsOptions.indexOf(1) : i) + 1)]); } },
              { label: 'Sync', value: `${syncOffsetMs > 0 ? '+' : ''}${syncOffsetMs} ms`,
                onDec: () => setSyncOffsetMs(syncOffsetMs - 5), onInc: () => setSyncOffsetMs(syncOffsetMs + 5),
                onCommitValue: (raw) => { const v = parseFloat(raw.replace(/ms/i, '').trim()); if (Number.isFinite(v)) setSyncOffsetMs(v); },
                editTitle: 'A/V latency (ms). Increase if notes hit the strike before you hear the sound.' },
            ]}
            onAutoBpm={handleAutoBpm}
            autoBpmBusy={bpmStage === 'detecting' || bpmStage === 'verifying'}
            onAutoOffset={handleAutoOffset}
            autoOffsetBusy={autoOffsetBusy}
            autoDisabled={!audioUrl}
          />
        </Group>

        <Group label="Volume">
          <VolumeControls music={musicVolume} sfx={sfxVolume} onMusic={setMusicVolume} onSfx={setSfxVolume} />
        </Group>

        <Group label="Camera View">
          <div style={cameraGridStyle}>
            <CameraBtn
              active={cameraView === 'default'}
              icon="◎"
              label="Default"
              onClick={() => setCameraView('default')}
            />
            <CameraBtn
              active={cameraView === 'left' || cameraView === 'right'}
              icon={(
                <span style={{ display: 'flex', gap: 2 }}>
                  <span style={{ opacity: cameraView === 'left' ? 1 : 0.3 }}>◀</span>
                  <span style={{ opacity: cameraView === 'right' ? 1 : 0.3 }}>▶</span>
                </span>
              )}
              label="Sides"
              onClick={() => setCameraView(cameraView === 'left' ? 'right' : 'left')}
            />
            <CameraBtn
              active={cameraView === 'free'}
              icon="⬡"
              label="Free"
              onClick={() => setCameraView('free')}
            />
            <CameraBtn
              active={cameraView === 'custom'}
              icon="☆"
              label="Custom"
              onClick={() => setCameraView('custom')}
            />
          </div>
          {cameraView === 'free' && (
            <Tooltip content={tip({ title: 'Save Camera View', body: "Saves the current free-look camera angle as your Custom preset. Switch to Custom view anytime to jump back to this exact angle." })}>
              <button
                onClick={requestSaveCustomCamera}
                style={{ ...cameraActionBtnStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <IcSave size={13} /> Save View
              </button>
            </Tooltip>
          )}
          {customCameraPosition && cameraView === 'custom' && (
            <Tooltip content={tip({ title: 'Clear Custom View', body: "Deletes the saved Custom camera angle. The Custom slot will be empty until you save a new Free view position." })}>
              <button
                onClick={clearCustomCamera}
                style={{ ...cameraActionBtnStyle, color: '#F87171', borderColor: 'rgba(248, 113, 113, 0.25)' }}
              >
                ✕ Clear Custom
              </button>
            </Tooltip>
          )}
        </Group>


        {/* Transport panel — lives below Camera View, matching SR layout */}
        <FlowTransportPanel />

        {/* Map options — collapsible like the others, but pinned to the bottom
            of the sidebar via a high flex order. */}
        <Group label="Map" pinnedOrder={999}>
          {/* Standard New/Open/Save/Clear grid (shared with the Synth VR editor). */}
          <MapGridButtons
            onNew={() => setNewMapDialogOpen(true)}
            onOpen={() => setLoadDialogOpen(true)}
            onSave={handleSave}
            onExport={() => setExportOpen(true)}
            onClear={handleClearChart}
          />
        </Group>
      </div>

      </aside>

      <main
        // position:relative + gap:0 so the highway fills the whole centre column
        // (down to the bottom edge) and the playback bar floats over it (absolute),
        // like the Synth VR editor — instead of the highway being boxed above it.
        style={{ ...centerStyle, position: 'relative', gap: 0 }}
        onDragOver={(e) => {
          if (!dragHasFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          if (!chartDragActive) setChartDragActive(true);
        }}
        onDragLeave={(e) => {
          // Ignore leave events fired while crossing into child elements.
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setChartDragActive(false);
        }}
        onDrop={handleChartDrop}
      >
        <div style={highwayHolder}>
          <FlowScene />
          {/* Performance pill — live FPS + Auto/Perf/Quality density modes,
              same control as the VR editor (top-centre of the highway). */}
          <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 13, pointerEvents: 'auto' }}>
            <PerfPill />
          </div>
          {showOffsetWarn && (
            <div style={offsetWarnStyle}>You can't place notes in the offset area</div>
          )}
          {showEditBlockWarn && (
            <div style={offsetWarnStyle}>
              {playTest
                ? 'Play Test is running — press Esc to go back to editing'
                : `Edit window is on — you can only edit within ${EDIT_WINDOW_BEATS} beats of the playhead (E to unlock)`}
            </div>
          )}
          {/* Play Test HUD. Scored run = full score readout; practice = a slim
              badge (no score — you're just feeling the map out). */}
          {playTest && (playTestScored ? (
            <div style={playTestHudStyle}>
              <span style={{ letterSpacing: 1.2, fontWeight: 800 }}>SCORED RUN</span>
              <span style={playTestStatStyle}>
                <b style={{ color: '#4ADE80' }}>{playTestStats.hits}</b> hit
              </span>
              <span style={playTestStatStyle}>
                <b style={{ color: '#F87171' }}>{playTestStats.misses}</b> miss
              </span>
              <span style={{ ...playTestStatStyle, opacity: 0.75 }}>
                {playTestStats.hits + playTestStats.misses > 0
                  ? `${Math.round((playTestStats.hits / (playTestStats.hits + playTestStats.misses)) * 100)}%`
                  : '—'}
              </span>
              <span style={{ opacity: 0.6, fontSize: 9 }}>Esc to exit</span>
            </div>
          ) : (
            <div style={playTestHudStyle}>
              <span style={{ letterSpacing: 1.2, fontWeight: 800 }}>PRACTICE</span>
              <span style={{ opacity: 0.7, fontSize: 10 }}>scrub anywhere · no score</span>
              <span style={{ opacity: 0.6, fontSize: 9 }}>Esc to exit</span>
            </div>
          ))}
          {chartDragActive && (
            <div style={chartDropOverlayStyle}>
              <div style={chartDropCardStyle}>
                <div style={{ fontSize: 30, marginBottom: 6 }}>📂</div>
                Drop a <strong>.flow</strong> map to open it
              </div>
            </div>
          )}
        </div>

        {/* Bottom playback bar — floats over the highway's lower edge (absolute),
            so the 3D fills the column to the bottom. Shared VR-style layout. */}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 8, zIndex: 10 }}>
          <EditorPlaybackBar
            playing={playing}
            onTogglePlay={() => setPlaying(!playing)}
            onRewind={() => seek(0)}
            currentTime={currentTime}
            duration={duration}
            coverUrl={coverUrl}
            primary={songName.trim() || (audioName ? audioName.replace(/\.[^.]+$/, '') : '(untitled)')}
            secondary={songAuthor.trim() || 'Unknown artist'}
            onSongClick={() => useFlowStore.getState().setEditMapInfoOpen(true)}
            waveform={<FlowTimeline />}
            spectrum={<AudioBars width={56} height={24} getData={engine.getFlowFrequencyData} />}
            hint={<FlowHelpHints />}
          />
        </div>
      </main>

      {/* Music plays through the Web Audio engine (flowAudioEngine), not an
          <audio> element — the AudioContext clock is the sample-accurate master
          timeline for the highway/timeline. */}

      {/* ── Audio sync debug console (Ctrl+Shift+D or the 🐞 transport button) ── */}
      {debugSync && (
        <FloatingWindow
          id="sync"
          title="Audio Sync Debug"
          accent="var(--accent)"
          defaultRect={{ x: 12, y: Math.max(60, window.innerHeight - 240), w: 520, h: 220 }}
          minW={320}
          minH={140}
          onClose={() => setDebugSync(false)}
        >
          <div style={debugBodyStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 }}>
              <span style={{ color: color.inkFaint }}>
                {debugBufferRef.current.length} samples · {playing ? 'playing' : 'paused'}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  style={debugBtnStyle}
                  onClick={() => {
                    const text = debugBufferRef.current.join('\n');
                    const done = () => { setDebugCopied(true); setTimeout(() => setDebugCopied(false), 1500); };
                    navigator.clipboard?.writeText(text).then(done).catch(() => {
                      // Fallback for non-secure contexts.
                      const ta = document.createElement('textarea');
                      ta.value = text; document.body.appendChild(ta); ta.select();
                      try { document.execCommand('copy'); done(); } catch { /* ignore */ }
                      document.body.removeChild(ta);
                    });
                  }}
                >
                  {debugCopied ? '✓ Copied' : 'Copy'}
                </button>
                <button
                  style={debugBtnStyle}
                  onClick={() => { debugBufferRef.current = []; setDebugLines([]); }}
                >
                  Clear
                </button>
              </div>
            </div>
            {/* Headline metrics — the two the user asked to see at a glance. */}
            <div style={debugStatsRowStyle}>
              <div style={debugStatStyle}>
                <span style={debugStatLabel}>OUTPUT LATENCY</span>
                <span style={{ ...debugStatVal, color: 'var(--accent)' }}>
                  {debugStats ? `${debugStats.outLatMs.toFixed(0)} ms` : '—'}
                </span>
              </div>
              <div style={debugStatStyle}>
                <span style={debugStatLabel}>HEARD − AUDIO</span>
                <span style={{ ...debugStatVal, color: color.accentSoft }}>
                  {debugStats ? `${debugStats.heardDeltaMs > 0 ? '+' : ''}${debugStats.heardDeltaMs.toFixed(0)} ms` : '—'}
                </span>
              </div>
              <div style={debugStatStyle}>
                <span style={debugStatLabel}>CLOCK RATE</span>
                <span style={{ ...debugStatVal, color: debugStats && Math.abs(debugStats.cumRate - 1) < 0.01 ? '#34D399' : '#F59E0B' }}>
                  {debugStats ? debugStats.cumRate.toFixed(3) : '—'}
                </span>
              </div>
            </div>
            <div style={{ color: color.inkDim, marginBottom: 6, lineHeight: 1.5 }}>
              rate ≈ 1.000 = no drift · heard−audio ≈ −output latency (auto-compensated)
            </div>
            {debugLines.length === 0
              ? <div style={{ color: color.inkFaint }}>Press play to start sampling…</div>
              : debugLines.map((l, i) => (
                  <div key={i} style={{ opacity: i === 0 ? 1 : 0.5 }}>{l}</div>
                ))}
          </div>
        </FloatingWindow>
      )}

      <aside style={{ ...rightPanelStyle, overflowY: 'visible', gap: 0, marginBottom: 'var(--shell-pad-y, 16px)' }}>
        {/* Top-right action row — circular buttons (same style as the Back
            button). Song info now lives in the bottom playback bar. 38px tall to
            stay symmetric with the left header's first row. */}
        <div style={{ flexShrink: 0, padding: '0 11px 0 10px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={toggleFullscreen} title="Toggle fullscreen" style={headerCircleBtn(false)}>
            <IcFullscreen size={18} />
          </button>
          <button onClick={() => setQrOpen(true)} title="Play on phone" style={headerCircleBtn(false)}>
            <IcPhone size={18} />
          </button>
          <button onClick={() => setSettingsOpen(!settingsOpen)} title="Editor settings" style={headerCircleBtn(settingsOpen)}>
            <IcSettings size={18} />
          </button>
        </div>
        <div ref={rightBarRef} className="overlay-scroll" style={{ ...sidebarScrollStyle, padding: '0 11px 0 10px' }}>
        <ScrollArrows targetRef={rightBarRef} />
        <ScrollFade targetRef={rightBarRef} />
        <OverlayScrollbar targetRef={rightBarRef} side="right" rightInset={5} />
        {/* Minimap rides at the TOP of the scrollable column (not pinned to the
            bottom) so it flows with the rest and never gets cut off. */}
        <Group
          label="Minimap"
          sidebar="right"
          headerAction={(
            <Tooltip content={tip({ title: 'Minimap Options', body: 'Camera height, position and rotation — plus the zoom that spreads packed notes apart.' })}>
              <button
                type="button"
                onClick={() => useFlowStore.getState().setMinimapCamWinOpen(!useFlowStore.getState().minimapCamWinOpen)}
                style={minimapGearBtnStyle}
                aria-label="Minimap options"
              >
                <IcSettings size={13} />
              </button>
            </Tooltip>
          )}
        >
          <FlowMinimap />
        </Group>

        {/* Visualizer — shared panel (one component in every editor). */}
        <Group label="Visualizer" sidebar="right">
          <VisualizerPanel />
        </Group>

        <Group label="Difficulty" sidebar="right">
          {(() => {
            // 5 defaults always shown (a missing one is created on select);
            // any custom-named difficulties present are appended.
            const existing = new Set<string>([activeDifficulty, ...Object.keys(chart.difficultyNotes ?? {})]);
            const customs = [...existing].filter(d => !FLOW_DIFFICULTIES.includes(d));
            const all = [...FLOW_DIFFICULTIES, ...customs];
            const items = all.map(d => ({
              id: d,
              label: d,
              count: d === activeDifficulty ? chart.notes.length : (chart.difficultyNotes?.[d]?.length ?? 0),
              present: existing.has(d),
            }));
            return (
              <DifficultyBar
                items={items}
                activeId={activeDifficulty}
                onSelect={setActiveDifficulty}
                onCreate={setActiveDifficulty}
                onAddCustom={() => { const n = window.prompt('New difficulty name:'); if (n && n.trim()) addDifficulty(n.trim()); }}
                // Lanes variant — Flow-only. Switching with notes present prompts
                // a redistribution confirmation.
                lanes={{
                  value: activeLaneCount,
                  options: [3, 4, 5],
                  onChange: (n) => {
                    if (n === activeLaneCount) return;
                    if (chart.notes.length > 0) setLaneSwitchTarget(n as FlowLaneCount);
                    else setActiveLaneCount(n as FlowLaneCount);
                  },
                }}
              />
            );
          })()}
        </Group>

        <Group label="Notes" sidebar="right">
          <div style={brushRowStyle}>
            {NOTE_TYPE_INFO.map(info => {
              const active = noteTypeBrush === info.key;
              const action = NOTE_BRUSH_ACTION[info.key];
              return (
                <Tooltip
                  key={info.key}
                  style={{ display: 'block', width: '100%' }}
                  content={noteBrushTip(info.label, info.tooltip, action, keybindings[action])}
                >
                  <button
                    // Clicking the ACTIVE Trail card exits back to note mode —
                    // same toggle feel as the T key (reviewer ask: the button
                    // should behave like the hotkey, committing the pending
                    // trail and returning to notes).
                    onClick={() => setNoteTypeBrush(active && info.key === 'trail' ? 'tap' : info.key)}
                    style={{
                      ...brushCardStyle,
                      width: '100%',
                      ...flowBtnState(active),
                    }}
                  >
                    <div style={{
                      ...brushCardTitleStyle,
                      color: active ? color.accent : color.inkFaint,
                    }}>{info.label}</div>
                    <div style={brushCardDescStyle}>{info.short}</div>
                  </button>
                </Tooltip>
              );
            })}
          </div>
          {/* Flick notes live inside the Notes group (same brush, directional). */}
          <div style={{ ...ui.subLabel, margin: '8px 0 4px', textAlign: 'center' }}>Flick Notes</div>
          <div style={dirNoteRowStyle}>
            {FLICK_NOTE_INFO.map(info => {
              const active = noteTypeBrush === info.key;
              const FlickIcon = FLICK_ICON[info.key];
              const action = NOTE_BRUSH_ACTION[info.key];
              return (
                <Tooltip
                  key={info.key}
                  style={{ display: 'block', width: '100%' }}
                  content={noteBrushTip(`${info.label} Flick`, info.tooltip, action, keybindings[action])}
                >
                  <button
                    onClick={() => setNoteTypeBrush(info.key)}
                    style={{
                      ...dirNoteBtnStyle,
                      width: '100%',
                      ...flowBtnState(active),
                    }}
                  >
                    <div style={dirNoteGlyphStyle}><FlickIcon size={20} /></div>
                    <div style={dirNoteLabelStyle}>{info.label}</div>
                  </button>
                </Tooltip>
              );
            })}
          </div>
          {/* Mirror placement — segmented pill (same style as the Visualizer's
              React-to / Camera controls). Mirrors every placed note/trail across
              the centre line (lane 0↔3, 1↔2). */}
          <Tooltip content={tip({ title: 'Mirror', body: 'Mirror mirrors every note and trail you place across the centre line — great for symmetric patterns. Normal places notes exactly where you click.' })}>
            <div style={reactSegStyle}>
              <span style={reactSegLabelStyle}>Mirror</span>
              {([['mirror', 'On', true], ['normal', 'Off', false]] as const).map(([key, label, on]) => (
                <button
                  key={key}
                  onClick={() => setNoteMirror(on)}
                  style={{ ...reactSegBtnStyle, ...(noteMirror === on ? reactSegBtnActiveStyle : null) }}
                >
                  {label}
                </button>
              ))}
            </div>
          </Tooltip>
        </Group>

        </div>
      </aside>

      {/* Save / load result banner — floats above the highway floor so the
          sidebar doesn't have to reserve any space for status. Errors trump
          success messages when both are set. */}
      {(chartError || chartStatus || audioError) && (
        <div style={(chartError || audioError) ? toastErrorStyle : toastStyle}>
          {chartError || audioError || chartStatus}
        </div>
      )}

      {/* Server save failed: offer an explicit local-backup download instead of
          silently saving a file to disk. */}
      {serverDownPrompt && (
        <div
          style={{
            position: 'fixed', left: 250, bottom: 192, zIndex: 4001,
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px', borderRadius: 12,
            fontFamily: font.sans, fontSize: 12, fontWeight: 700,
            background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.96)',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
          }}
        >
          <button
            onClick={() => void downloadLocalBackup()}
            style={{
              cursor: 'pointer', padding: '5px 12px', borderRadius: 10,
              border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.6)',
              background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25)', color: '#F5E8FF',
              fontWeight: 700, fontFamily: font.sans, fontSize: 12,
            }}
          >
            Download backup
          </button>
          <button
            onClick={() => { setServerDownPrompt(false); setChartError(null); }}
            style={{
              cursor: 'pointer', padding: '5px 12px', borderRadius: 10,
              border: '1px solid rgba(255, 255, 255, 0.18)',
              background: 'transparent', color: '#C7C7D9',
              fontWeight: 700, fontFamily: font.sans, fontSize: 12,
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Floating minimap-camera tuning window (self-gating on store flag). */}
      <FlowMinimapCamWindow />

      {saveDialogOpen && (
        <SaveAsDialog
          initialName={
            savedAsName
            ?? (songName.trim() || (audioName ? audioName.replace(/\.[^.]+$/, '') : 'untitled'))
          }
          targetLabel={localFolder ? `your folder (${localFolder})` : undefined}
          onCancel={() => setSaveDialogOpen(false)}
          onConfirm={(name) => {
            setSaveDialogOpen(false);
            void performSave(name);
          }}
        />
      )}

      {loadDialogOpen && (
        <LoadLibraryDialog
          onClose={() => setLoadDialogOpen(false)}
          onUploadClick={() => {
            setLoadDialogOpen(false);
            chartInputRef.current?.click();
          }}
          localFolderName={localFolder}
          onConnectFolder={handleConnectFolder}
          onPickLocal={handlePickFromFolder}
        />
      )}

      {/* Editor settings — rendered at the shell level (NOT inside the left
          sidebar's transport panel) so the top-right gear always opens it even
          when the left sidebar is collapsed. */}
      <FlowSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <BenchmarkOverlay />

      {qrOpen && (() => {
        // The phone player owns its own gameplay tuning now (its in-game ⚙),
        // so the QR just opens /play.
        const playUrl = `${window.location.origin}/play`;
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        return (
          <div style={bpmPopupBackdrop} onClick={() => setQrOpen(false)}>
            <div style={{ ...bpmPopupCard, maxWidth: 360, position: 'relative' }} onClick={e => e.stopPropagation()}>
              <button
                aria-label="Close"
                onClick={() => setQrOpen(false)}
                onMouseDown={e => e.stopPropagation()}
                style={{
                  position: 'absolute', top: 14, right: 14,
                  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%', background: 'transparent',
                  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)',
                  color: 'var(--accent)', cursor: 'pointer', flexShrink: 0,
                  transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--accent)';
                  e.currentTarget.style.borderColor = 'var(--accent)';
                  e.currentTarget.style.color = 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)';
                  e.currentTarget.style.color = 'var(--accent)';
                }}
              ><IcClose size={15} /></button>
              <div style={bpmPopupTitle}>Play on phone</div>
              <div style={{ background: '#fff', padding: 14, borderRadius: 12, margin: '14px auto', width: 'fit-content' }}>
                <QRCodeSVG value={playUrl} size={196} />
              </div>
              <div style={{ ...bpmPopupSub, wordBreak: 'break-all' }}>{playUrl}</div>
              <div style={{ ...bpmPopupSub, fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
                Scan with your phone (same Wi-Fi as this PC). Pick a song, difficulty and play.
                {isLocalhost && (
                  <><br /><b style={{ color: '#F59E0B' }}>Heads-up:</b> you opened the editor on <b>localhost</b>,
                  so this QR points to localhost and won't load on the phone. Open the editor via your PC's
                  LAN IP (e.g. http://192.168.x.x:3112) and reopen this dialog.</>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {newMapDialogOpen && (
        <NewMapDialog
          onClose={() => setNewMapDialogOpen(false)}
          onCreated={(projectName) => setSavedAsName(projectName?.trim() ? projectName.trim() : null)}
        />
      )}

      <ExportVisualizerDialog
        open={exportOpen}
        mapName={songName.trim() || undefined}
        onCancel={() => setExportOpen(false)}
        onConfirm={(vis) => {
          setExportOpen(false);
          void downloadCurrentChart(vis).then(fn => {
            setChartStatus(`Exported ${fn}`);
            setTimeout(() => setChartStatus(null), 4000);
          }).catch(() => setChartError('Export failed.'));
        }}
      />

      {editMapInfoOpen && (
        <EditMapInfoDialog
          onClose={() => setEditMapInfoOpen(false)}
          audioLoading={audioLoading}
          onReplaceAudio={() => fileInputRef.current?.click()}
          onSave={async (next) => {
            // Apply edits to the store first so the banner reflects them
            // immediately, regardless of whether the server save succeeds.
            const store = useFlowStore.getState();
            store.setSongName(next.songName);
            store.setSongAuthor(next.songAuthor);
            if (next.cover) {
              store.setCover({
                url: next.cover.url,
                name: next.cover.name,
                mime: next.cover.mime,
                bytes: next.cover.bytes,
              });
            } else if (next.removeCover) {
              store.clearCover();
            }
            // Re-save to the server using the previously-saved name when
            // available so we don't fork a second file under a new name.
            // If the chart was never saved, fall back to the (possibly new)
            // song name so the user still gets a persisted file.
            const targetName = savedAsName ?? (next.songName.trim() || null);
            await performSave(targetName ?? undefined);
            setEditMapInfoOpen(false);
          }}
        />
      )}

      {/* Lane-count change → warn that notes get redistributed. */}
      {laneSwitchTarget !== null && (
        <div style={bpmPopupBackdrop} onClick={() => setLaneSwitchTarget(null)}>
          <div style={bpmPopupCard} onClick={e => e.stopPropagation()}>
            <div style={bpmPopupTitle}>Switch {activeDifficulty} to {laneSwitchTarget} lanes?</div>
            <div style={bpmPopupSub}>
              {activeDifficulty}'s <b style={{ color: color.ink }}>{chart.notes.length}</b> notes will be
              redistributed from {activeLaneCount} to {laneSwitchTarget} lanes. The pattern is kept
              proportionally but <b style={{ color: '#F59E0B' }}>may need manual adjustment</b> (some
              notes can land on the same spot).
            </div>
            <div style={bpmPopupRow}>
              <button
                style={{ ...bpmApplyBtnStyle, borderColor: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)', color: color.inkDim }}
                onClick={() => setLaneSwitchTarget(null)}
              >
                Cancel
              </button>
              <button
                style={{ ...bpmApplyBtnStyle, borderColor: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)', color: color.accent }}
                onClick={() => { setActiveLaneCount(laneSwitchTarget); setLaneSwitchTarget(null); }}
              >
                Redistribute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auto-BPM popup — shared dialog; Flow drives it with the full flow
          (detect → verify online → choose → done). */}
      <AutoBpmDialog
        stage={bpmStage}
        detected={bpmDetected}
        final={bpmFinal}
        online={bpmOnline}
        onlineNote={bpmOnlineNote}
        confidence={bpmConfidence}
        candidates={bpmCandidates}
        reconciled={bpmReconciled}
        onChoose={chooseBpm}
        onClose={() => setBpmStage(null)}
      />
    </div>
  );
}


// ── Contextual help bar ──────────────────────────────────────────────────
//
// A single always-visible hint line above the timeline that reflects what the
// user can do RIGHT NOW: it reacts to the active brush, an in-progress trail,
// an active selection, and the modifier keys currently held (Ctrl/Alt/Shift).
const FLOW_HINT_PIN_KEY = 'flow-hint-pinned';

function FlowHelpHints() {
  const brush        = useFlowStore(s => s.noteTypeBrush);
  const pendingTrail = useFlowStore(s => s.pendingTrailNoteId);
  const selCount     = useFlowStore(s => s.selection.noteIds.size);
  const cutCount     = useFlowStore(s => s.cutNoteIds.size);
  const hintHidden   = useFlowStore(s => s.hintBarHidden);
  const setHintHidden = useFlowStore(s => s.setHintBarHidden);
  const [mods, setMods] = React.useState({ shift: false, ctrl: false, alt: false });
  // Pin / auto-hide toggle — identical to the Synth VR hint bar.
  const [pinned, setPinned] = React.useState<boolean>(() => {
    try { return localStorage.getItem(FLOW_HINT_PIN_KEY) !== '0'; } catch { return true; }
  });
  const [hovered, setHovered] = React.useState(false);
  const [keyRecent, setKeyRecent] = React.useState(false);
  // "All shortcuts" — the hint bar expands into the full keyboard + mouse sheet.
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const hideTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const sync = (e: KeyboardEvent) => {
      setMods({ shift: e.shiftKey, ctrl: e.ctrlKey || e.metaKey, alt: e.altKey });
      // Any key wakes the hint for 2s (only matters in auto-hide mode).
      setKeyRecent(true);
      window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setKeyRecent(false), 2000);
    };
    const clear = () => setMods({ shift: false, ctrl: false, alt: false });
    window.addEventListener('keydown', sync);
    window.addEventListener('keyup', sync);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', sync);
      window.removeEventListener('keyup', sync);
      window.removeEventListener('blur', clear);
      window.clearTimeout(hideTimer.current);
    };
  }, []);

  const togglePinned = () => {
    setPinned(p => {
      const next = !p;
      try { localStorage.setItem(FLOW_HINT_PIN_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Priority: trail-in-progress → selection → held modifier → active brush.
  let hint: string;
  let accent = 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)';
  if (pendingTrail) {
    hint = 'Trail in progress — click to add waypoints · Shift+Click to finish where you want the trail to end · Esc to cancel';
    accent = 'rgba(232, 121, 249, 0.45)';
  } else if (selCount > 0) {
    hint = `${selCount} note${selCount > 1 ? 's' : ''} selected — Ctrl+C copy · Ctrl+X cut · Del remove · ↑/↓ nudge (Shift = ×4) · Alt+drag to move · click empty floor to deselect`;
    accent = 'rgba(34, 197, 94, 0.45)';
  } else if (mods.alt) {
    hint = 'Alt — drag any note or trail waypoint to move it (snaps to grid)';
    accent = 'rgba(251, 191, 36, 0.45)';
  } else if (mods.ctrl) {
    hint = `Ctrl — wheel to change snap · Ctrl+Z undo · Ctrl+Shift+Z redo${cutCount > 0 || selCount > 0 ? ' · Ctrl+C/X copy/cut' : ''} · Ctrl+V paste at playhead`;
    accent = 'rgba(96, 165, 250, 0.45)';
  } else if (mods.shift) {
    hint = 'Shift — in Trail mode, Shift+Click ends the trail at that point';
    accent = 'rgba(232, 121, 249, 0.45)';
  } else if (brush === 'trail') {
    hint = 'Trail — click a lane to place the first note, click to add waypoints, then Shift+Click where you want the trail to end';
    accent = 'rgba(232, 121, 249, 0.45)';
  } else if (brush === 'arrow-left' || brush === 'arrow-center' || brush === 'arrow-right') {
    hint = 'Arrow note — click a lane to place a directional note · click a note to remove it · wheel to scrub';
  } else {
    hint = 'Tap — click a lane to place a note · click a note to remove it · wheel to scrub · left-drag to box-select · right-drag to set a teal loop (right-click to clear)';
  }

  // Closed outright (✕) → gone until re-enabled in Settings → UI.
  if (hintHidden) return null;

  // Visible if pinned, or (auto mode) while hovered or within 2s of a keypress.
  // An open shortcut sheet pins it too — the sheet lives inside the bar, so a
  // fade-out would take the whole reference with it.
  const shown = pinned || hovered || keyRecent || sheetOpen;

  return (
    <div
      style={{
        ...helpBarStyle,
        borderColor: accent,
        opacity: shown ? 1 : 0,
        // When faded out it must not swallow clicks meant for the highway —
        // an invisible bar was blocking the note-placement area (user report).
        pointerEvents: shown ? 'auto' : 'none',
        transition: 'opacity 0.4s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ opacity: 0.85, flexShrink: 0 }}>💡</span>
      {/* The hint truncates; the buttons never leave the bar. On narrow
          screens the long hint used to push "All shortcuts" clean out of view
          (reviewer report: some resolutions couldn't see the button). */}
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={hint}>{hint}</span>
      {sheetOpen && <ShortcutCheatSheet onClose={() => setSheetOpen(false)} />}
      <button
        onClick={() => setSheetOpen(o => !o)}
        title="Show every shortcut (keyboard + mouse)"
        style={{
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 4,
          background: 'transparent', border: 'none', padding: '2px 4px',
          cursor: 'pointer', color: 'var(--accent)',
          fontFamily: font.sans, fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
          opacity: sheetOpen ? 1 : 0.75,
        }}
      >
        All shortcuts {sheetOpen ? '▾' : '▴'}
      </button>
      <button
        onClick={togglePinned}
        title={pinned
          ? 'Tooltip pinned (always visible) — click to auto-hide'
          : 'Tooltip auto-hides — click to keep it always visible'}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          padding: 2,
          cursor: 'pointer',
          color: 'var(--accent)',
          opacity: pinned ? 1 : 0.35,
          filter: pinned ? 'drop-shadow(0 0 5px var(--accent))' : 'none',
          transition: 'opacity 0.15s ease, filter 0.15s ease',
        }}
      >
        <IcKeyboard size={15} />
      </button>
      <button
        onClick={() => setHintHidden(true)}
        title="Close the hint bar (bring it back in Settings > UI)"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent', border: 'none', padding: 2,
          cursor: 'pointer', color: color.inkFaint, fontSize: 13, lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}


/**
 * Full shortcut reference — every rebindable action with its CURRENT combo,
 * plus the fixed mouse/wheel gestures. Expands out of the hint bar ("All
 * shortcuts") so the one-line hint stays short but nothing is hidden.
 */
function ShortcutCheatSheet({ onClose }: { onClose: () => void }) {
  const keybindings = useFlowStore(s => s.keybindings);
  const setSettingsTab = useFlowStore(s => s.setSettingsTab);
  const setSettingsOpen = useFlowStore(s => s.setSettingsOpen);

  // Esc closes the sheet (before anything else can eat the key).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const rows = (items: { label: string; keys: string }[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {items.map(it => (
        <div key={it.label} style={cheatRowStyle}>
          <span style={{ color: color.inkDim }}>{it.label}</span>
          <kbd style={cheatKeyStyle}>{it.keys}</kbd>
        </div>
      ))}
    </div>
  );

  return (
    <div style={cheatSheetStyle} onMouseDown={e => e.stopPropagation()}>
      <div style={cheatHeadStyle}>
        <span style={{ fontWeight: 800, letterSpacing: 1, fontSize: 11 }}>ALL SHORTCUTS</span>
        <button
          onClick={() => { setSettingsTab('keys'); setSettingsOpen(true); onClose(); }}
          style={cheatLinkStyle}
        >
          Rebind in Settings → Keys
        </button>
        <button onClick={onClose} style={{ ...cheatLinkStyle, opacity: 0.7 }} title="Close">×</button>
      </div>
      <div style={cheatGridStyle}>
        {KEYBIND_SECTIONS.map(sec => (
          <div key={sec.title} style={cheatColStyle}>
            <div style={cheatTitleStyle}>{sec.title}</div>
            {rows(sec.items.map(i => ({
              label: i.label,
              keys: prettyCombo(keybindings[i.action] || '—'),
            })))}
          </div>
        ))}
        <div style={cheatColStyle}>
          <div style={cheatTitleStyle}>Mouse</div>
          {rows(MOUSE_REFERENCE.map(m => ({ label: m.desc, keys: m.keys })))}
        </div>
      </div>
    </div>
  );
}

const cheatSheetStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 'calc(100% + 8px)',
  maxHeight: '58vh',
  overflowY: 'auto',
  padding: '12px 14px 14px',
  borderRadius: 14,
  fontFamily: font.sans,
  background: 'oklch(0.14 calc(0.03 * var(--tint-c)) var(--tint-h) / 0.97)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.35)',
  boxShadow: '0 18px 46px oklch(0 0 0 / 0.55)',
  zIndex: 40,
  cursor: 'default',
};

const cheatHeadStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginBottom: 10,
  color: color.accentSoft,
};

const cheatGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
  gap: '12px 22px',
  alignItems: 'start',
};

const cheatColStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5 };

const cheatTitleStyle: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 800,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: color.accentSoft,
  opacity: 0.9,
};

const cheatRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 10,
  fontSize: 10.5,
  lineHeight: 1.35,
};

const cheatKeyStyle: React.CSSProperties = {
  flexShrink: 0,
  fontFamily: 'monospace',
  fontSize: 10,
  fontWeight: 700,
  color: color.ink,
  padding: '1px 6px',
  borderRadius: 6,
  background: 'oklch(0.22 calc(0.03 * var(--tint-c)) var(--tint-h) / 0.8)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
};

const cheatLinkStyle: React.CSSProperties = {
  marginLeft: 'auto',
  background: 'transparent',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: font.sans,
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--accent)',
};

function CameraBtn({
  active, icon, label, onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  // Rest style only — hover is the shared global tint wash (see tintHover.ts), so
  // camera buttons hover identically to every other button. The SELECTED button
  // gets the accent outline.
  const REST_BG = 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)';
  const base: React.CSSProperties = {
    ...cameraBtnStyle,
    // Active = accent gradient fill + soft (0.5) accent border — IDENTICAL to the
    // Transport buttons' active look (and the Synth VR camera buttons), so every
    // "marked" toggle reads the same across the editor.
    background: active
      ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
      : REST_BG,
    border: active
      ? '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)'
      : '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
    color: active ? color.accent : color.inkFaint,
    boxShadow: 'none',
  };
  return (
    <button
      onClick={onClick}
      style={base}
      title={label}
    >
      <span className="ic-themed" style={{ fontSize: 12 }}>{icon}</span>
      <span style={{ fontSize: 9 }}>{label}</span>
    </button>
  );
}

/** Show BPM with up to 2 decimals, but drop trailing zeros (129, 128.5, 128.73). */
function fmtBpm(bpm: number): string {
  return Number.isInteger(bpm) ? bpm.toFixed(0) : String(Math.round(bpm * 100) / 100);
}

// Steppers + Auto buttons now use the shared <ControlsPanel>.

// ── Styles ──────────────────────────────────────────────────────────────

// Inner body styling for the sync-debug window (the draggable/resizable frame
